import { useTranslation } from 'react-i18next';
import { PRODUCT_IMG } from '@/img/productImages';

export default function Hero() {
  const { t } = useTranslation('home');

  const stats = t('hero.stats', { returnObjects: true }) as Array<{ value: string; label: string }>;
  const quickSelectors = t('hero.quickSelectors', { returnObjects: true }) as Array<{ tag: string; name: string; price: string }>;

  return (
    <section id="top" className="relative bg-gradient-to-br from-slate-50 to-slate-100 pt-20 pb-12 md:pt-28 md:pb-16">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left content */}
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
                {t('hero.title')}
              </h1>
              <p className="text-base md:text-lg text-slate-600 mb-6 max-w-2xl leading-relaxed">
                {t('hero.desc')}
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <a
                  href="#compare"
                  className="inline-flex items-center text-sm font-medium bg-slate-900 text-white px-5 py-2.5 rounded-md hover:bg-brand-800 transition-colors whitespace-nowrap"
                >
                  {t('hero.viewQuote')}
                </a>
                <a
                  href="#products"
                  className="inline-flex items-center text-sm font-medium bg-white text-slate-900 border border-slate-200 px-5 py-2.5 rounded-md hover:bg-slate-50 transition-colors whitespace-nowrap"
                >
                  {t('hero.browseProducts')}
                </a>
              </div>

              {/* Core stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {stats.map((stat) => (
                  <div
                    key={stat.value}
                    className="bg-white border border-slate-200 rounded-lg p-3 md:p-4"
                  >
                    <div className="text-lg md:text-xl font-bold text-slate-900 mb-1">{stat.value}</div>
                    <div className="text-xs text-slate-500 leading-snug">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image + selector */}
            <div className="lg:w-[480px] flex flex-col gap-4">
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <img
                  src={PRODUCT_IMG.product005}
                  alt="RTK-982 Host"
                  className="w-full h-48 md:h-56 object-contain object-center bg-slate-50"
                />
                <div className="px-4 py-3 border-t border-slate-100">
                  <div className="text-sm font-semibold text-slate-900">{t('hero.productImageTitle')}</div>
                  <div className="text-xs text-slate-500">{t('hero.productImageSubtitle')}</div>
                </div>
              </div>

              {/* Quick selector */}
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{t('hero.quickSelectLabel')}</span>
                </div>
                {quickSelectors.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center justify-between px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                  >
                    <div>
                      <div className="text-xs text-slate-500 mb-0.5">{item.tag}</div>
                      <div className="text-sm font-medium text-slate-900">{item.name}</div>
                    </div>
                    <div className="text-sm font-bold text-orange-600">{item.price}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}